import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { isAppHostname } from '@/lib/brand';
import { en } from './resources/en';
import { zhCN } from './resources/zh-CN';
import {
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  isLocalizedPublicPath,
  normalizeLocale,
  resolveInitialLocale,
  type LocaleSignals,
  type SupportedLocale,
} from './locale';

function readStoredLocale(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function browserSignals(): LocaleSignals {
  if (typeof document === 'undefined') return {};
  if (
    typeof window !== 'undefined' &&
    (isAppHostname(window.location.hostname) ||
      !isLocalizedPublicPath(window.location.pathname))
  ) {
    return {};
  }
  return {
    stored: readStoredLocale(),
    edgeHint: document.documentElement.dataset.okraLocale,
  };
}

function applyDocumentLocale(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
}

const initialLocale = resolveInitialLocale(browserSignals());

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    'zh-CN': { translation: zhCN },
  },
  lng: initialLocale,
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LOCALES],
  load: 'currentOnly',
  initAsync: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

applyDocumentLocale(initialLocale);
i18n.on('languageChanged', (language) => {
  applyDocumentLocale(normalizeLocale(language) ?? 'en');
});

export function currentLocale(): SupportedLocale {
  return normalizeLocale(i18n.resolvedLanguage ?? i18n.language) ?? 'en';
}

export async function changeLocale(locale: SupportedLocale): Promise<void> {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // Locale still changes for this page when storage is unavailable.
    }
  }
  await i18n.changeLanguage(locale);
}

export { i18n, useTranslation };
export {
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  isLocalizedPublicPath,
  normalizeLocale,
  resolveInitialLocale,
  type LocaleSignals,
  type SupportedLocale,
} from './locale';
